import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import { ANALYTICS_QUERY } from "@pages/analytics/types";
import {
  IconCheck,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

type VersionType = {
  version_id: string;
  version_title: string;
  [key: string]: unknown;
};

export const VersionFilter = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { version, setVersion } = useAnalyticsStoreShallow((s) => ({
    version: s.version,
    setVersion: s.setVersion,
  }));

  const { data: response, isLoading } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.VERSIONS],
    enabled: isOpen,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleVersionChange = useCallback(
    (v: VersionType) => {
      setVersion({
        id: v.version_id,
        title: v.version_title,
      });
      setIsOpen(false);
    },
    [setVersion],
  );

  const handleClear = useCallback(() => {
    setVersion({ id: "", title: "" });
    setIsOpen(false);
  }, [setVersion]);

  const versionsData = response?.[ANALYTICS_QUERY.VERSIONS] as
    | VersionType[]
    | undefined;

  const versions = useMemo(() => {
    if (!versionsData) return [];

    // Get unique versions by version_id
    const uniqueVersions = Array.from(
      new Map(versionsData.map((v: VersionType) => [v.version_id, v])).values(),
    ) as VersionType[];

    return uniqueVersions;
  }, [versionsData]);

  // Filter cached versions by search term
  const filteredVersions = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return versions;
    const search = debouncedSearchTerm.toLowerCase();
    return versions.filter((v: VersionType) =>
      v.version_title.toLowerCase().includes(search),
    );
  }, [versions, debouncedSearchTerm]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
      }, 250);
    }
  }, []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
      triggerWidth
    >
      <Popover.Trigger
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer tw-max-w-40 active:!tw-outline-offset-0 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary -xl:tw-w-full -xl:tw-max-w-[700px]",
          {
            "!tw-outline-primary": isOpen || !!version.id,
          },
        )}
      >
        <span className="tw-block tw-px-1 tw-font-sans tw-truncate tw-text-sm tw-font-medium tw-text-blue">
          {version.title || "Version"}
        </span>
        <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        {version.id && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                handleClear();
              }
            }}
            role="button"
            tabIndex={0}
            className="tw-ml-1 tw-flex tw-h-full tw-items-center tw-justify-center tw-border-l tw-border-solid tw-border-primary tw-pl-2"
          >
            <IconX className="tw-size-4 tw-text-primary" />
          </div>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-rounded-lg !tw-w-auto tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
        <div className="tw-relative tw-h-9 tw-px-3 tw-border-b tw-border-b-solid tw-border-b-blue-100">
          <IconSearch className="tw-size-4 tw-text-blue-500 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3" />
          <input
            type="text"
            placeholder="Search version"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-pl-6 tw-outline-none tw-w-full tw-h-full tw-rounded-t-lg tw-text-sm tw-font-normal tw-text-blue placeholder:tw-text-blue-500"
          />
        </div>
        <ScrollArea maxHeight={250}>
          <div className="tw-p-1 tw-space-y-0.5 tw-min-w-0 tw-max-w-full">
            {isLoading ? (
              <VersionSkeleton />
            ) : filteredVersions.length === 0 ? (
              <div className="tw-py-3 tw-text-center tw-text-sm tw-text-blue-500">
                {debouncedSearchTerm
                  ? "No versions found"
                  : "No versions available"}
              </div>
            ) : (
              filteredVersions.map((v: VersionType) => (
                <button
                  type="button"
                  key={v.version_id}
                  onClick={() => handleVersionChange(v)}
                  className={cn(
                    "tw-flex tw-h-9 tw-w-52 tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-blue-100 hover:tw-bg-blue-100":
                        version.id === v.version_id,
                    },
                  )}
                >
                  <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                    {v.version_title}
                  </span>
                  {version.id === v.version_id && (
                    <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  );
});

const VERSION_SKELETON_WIDTHS = [91, 48, 76, 107, 35];

const VersionSkeleton = () => {
  return (
    <>
      {VERSION_SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
